import React, { Suspense } from 'react'
import { BrowserRouter } from 'react-router-dom'
import { I18nProvider } from '../_metronic/i18n/i18nProvider'
import { LayoutProvider, LayoutSplashScreen } from '../_metronic/layout/core'
import { Routes } from './routing/Routes'
import { Auth0Provider } from '@auth0/auth0-react'
import { AxiosInterceptor } from "../setup/axios/AxiosInterceptor"
import { CustomerProvider } from './contexts/CustomerContext';


type Props = {
  basename: string
}

const App: React.FC<Props> = ({ basename }) => {

  // Check for auth errors BEFORE rendering Auth0Provider
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');

    if (error === 'access_denied' && errorDescription) {
      // Check if it's a password expiration/reset required
      if (errorDescription.includes('[PASSWORD_EXPIRED]') || errorDescription.includes('[PASSWORD_RESET_REQUIRED]')) {
        console.log('Password expired/reset required - redirecting to Universal Login');
        
        // Clear all storage
        sessionStorage.clear();
        localStorage.clear();
        
        // Extract clean message
        const cleanMessage = errorDescription
          .replace('[PASSWORD_EXPIRED]', '')
          .replace('[PASSWORD_RESET_REQUIRED]', '')
          .trim();
        
        // Redirect DIRECTLY to Auth0 Universal Login with forgot password hint
        const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN || 'konfidency.us.auth0.com';
        const clientId = 'gNlboCgjC7I8rg4ofHmazO4C3jCTmE0M';
        const redirectUri = encodeURIComponent(window.location.origin);
        const audience = encodeURIComponent('https://reviews-api.konfidency.com.br');
        
        // Go to Universal Login with reset hint
        window.location.href = `https://${auth0Domain}/authorize?` +
          `client_id=${clientId}&` +
          `response_type=code&` +
          `redirect_uri=${redirectUri}&` +
          `audience=${audience}&` +
          `scope=openid%20profile%20email%20read:current_user%20update:current_user_metadata%20app_metadata&` +
          `screen_hint=signup&` +  // Shows "Forgot password" prominently
          `prompt=login`;  // Forces fresh login screen
        
        return; // Stop execution
      }
      
      // Other access_denied errors - store and show error page
      sessionStorage.setItem('auth_error', JSON.stringify({
        type: 'access_denied',
        message: errorDescription
      }));
      
      window.location.href = '/error/access-denied';
    }
  }, []);

  // Check if we're in error state
  const hasAuthError = sessionStorage.getItem('has_auth_error') === 'true';
  const isErrorPage = window.location.pathname.startsWith('/error');

  // Handle Auth0 redirect callback - simplified
  const onRedirectCallback = (appState: any) => {
    const urlParams = new URLSearchParams(window.location.search);
    const returnUrl = urlParams.get('returnUrl') || appState?.returnTo || '/';
    
    const error = urlParams.get('error');
    if (!error) {
      window.history.replaceState({}, document.title, returnUrl);
    }
  };

  // If we're on an error page, don't use Auth0Provider at all
  if (hasAuthError || isErrorPage) {
    return (
      <Suspense fallback={<LayoutSplashScreen />}>
        <BrowserRouter basename={basename}>
          <I18nProvider>
            <LayoutProvider>
              <Routes />
            </LayoutProvider>
          </I18nProvider>
        </BrowserRouter>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<LayoutSplashScreen />}>
      <BrowserRouter basename={basename}>
        <I18nProvider>
          <LayoutProvider>
            <Auth0Provider
              domain="konfidency.us.auth0.com"
              clientId="gNlboCgjC7I8rg4ofHmazO4C3jCTmE0M"
              audience="https://reviews-api.konfidency.com.br"
              scope="read:current_user update:current_user_metadata app_metadata"
              cacheLocation="localstorage"
              useRefreshTokens={true}
              redirectUri={window.location.origin + "?returnUrl=" + encodeURIComponent(window.location.pathname + window.location.search)}
              onRedirectCallback={onRedirectCallback}
            >
              <AxiosInterceptor>
                <CustomerProvider>
                  <Routes />
                </CustomerProvider>
              </AxiosInterceptor>
            </Auth0Provider>
          </LayoutProvider>
        </I18nProvider>
      </BrowserRouter>
    </Suspense>
  )
}

export { App }
